import { useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { clsx } from 'clsx'

import { Skeleton, useMediaQuery, useSidebar } from '@buffer-mono/popcorn'
import { getUrl } from '@buffer-mono/shared-utils/bibe'
import {
  BufferTrackerReact as BufferTracker,
  Product,
} from '@buffer-mono/tracking-plan'

import { useHasComposerAutoSave } from '~publish/components/Composer/store/hooks/useHasComposerAutoSave'
import {
  useAccount,
  useHasOrganizationAction,
  useIsFreeUser,
  useOrganizationId,
  useUnlockedChannels,
} from '~publish/contexts/AccountContext'
import { isEssentialsUser } from '~publish/helpers/user'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { useQueryParam } from '~publish/hooks/useQueryParam'
import { useStartPageSunsetAnnouncementEnabled } from '~publish/hooks/useStartPageSunsetAnnouncementEnabled'
import {
  createNew,
  settingsBillingRoute,
  settingsTeamRoute,
} from '~publish/legacy/routes'

import { SHORT_HEIGHT_MEDIA_QUERY } from '../../constants'
import { useDelayedExpand } from '../../hooks/useDelayedExpand'
import {
  useCanPostOnAnyChannel,
  useChannels,
} from '../ChannelsSection/useChannels'
import {
  GlobalButtonConnectChannelItem,
  GlobalButtonCreateIdeaItem,
  GlobalButtonCreatePostItem,
  GlobalButtonCreateStartPageItem,
  GlobalButtonInviteTeamMemberItem,
  GlobalButtonMenu,
  GlobalButtonMenuSeparator,
} from './GlobalButtonMenu'

import styles from './GlobalButton.module.css'

import type { JSX } from 'react'

const PUBLISH_CTA = 'publish-mainSidebar-globalButton-newPost-1'
const IDEA_CTA = 'publish-mainSidebar-globalButton-newIdea-1'
const CONNECT_CHANNEL_CTA = 'publish-mainSidebar-globalButton-connectChannel-1'
const INVITE_TEAM_CTA = 'publish-mainSidebar-globalButton-inviteTeam-1'
const CREATE_START_PAGE_LINK = getUrl(
  'https://start-page.buffer.com/onboarding',
)

export function useGlobalButtonCompact(): {
  isCompact: boolean
  showCollapsedUI: boolean
} {
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'
  const showCollapsedUI = useDelayedExpand(isCollapsed)
  const isShortHeight = useMediaQuery(SHORT_HEIGHT_MEDIA_QUERY)
  return { isCompact: showCollapsedUI || isShortHeight, showCollapsedUI }
}

export function GlobalButtonSkeleton({
  className,
}: {
  className?: string
}): JSX.Element {
  return (
    <output
      aria-label="Loading create menu"
      className={clsx(
        styles.globalButton,
        styles.globalButtonSkeleton,
        className,
      )}
    >
      <Skeleton width="100%" height="100%" rounded />
    </output>
  )
}

export function GlobalButton({
  className,
}: {
  className?: string
}): JSX.Element | null {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const { isCompact, showCollapsedUI } = useGlobalButtonCompact()

  const navigate = useNavigate()
  const openGlobalModal = useOpenGlobalModal()
  const { createNewPostInComposer } = usePostComposer()
  const [channelQueryParam = []] = useQueryParam<string[]>('channels')
  const { id: channelIdFromRoute } = useParams<{ id: string }>()
  const isFreeUser = useIsFreeUser()
  const unlockedChannels = useUnlockedChannels()
  const organizationId = useOrganizationId()
  const { account, loading: accountLoading } = useAccount()
  const { loading: channelsLoading } = useChannels()
  const canConnectChannel = useHasOrganizationAction('manageChannels')
  const canManageTeam = useHasOrganizationAction('manageTeamMembers')
  const canPostOnAnyChannel = useCanPostOnAnyChannel()
  const hasRecoverablePost = useHasComposerAutoSave()
  const isStartPageSunset = useStartPageSunsetAnnouncementEnabled()

  const commonTrackingProperties =
    account?.currentOrganization?.commonTrackingProperties

  // Free users need to upgrade to invite team members
  const showInviteTeamUpgradePath = isFreeUser
  // Essentials users need to upgrade to Team plan to invite team members
  const isEssentials = isEssentialsUser(account)

  // Track when dropdown opens
  const handleDropdownOpenChange = (): void => {
    BufferTracker.globalPlusButtonOpened({
      organizationId: organizationId ?? '',
      product: Product.Publish,
      cta: 'publish-mainSidebar-globalButton-opened-1',
      clientName: 'publishWeb',
      upgradePathName: 'globalButton-upgrade',
      ...commonTrackingProperties,
    })
  }

  // Track when a dropdown item is clicked
  const trackItemClick = (ctaButton: string): void => {
    BufferTracker.globalPlusButtonItemClicked({
      organizationId: organizationId ?? '',
      product: Product.Publish,
      cta: `publish-mainSidebar-globalButton-${ctaButton}-1`,
      clientName: 'publishWeb',
      upgradePathName: 'globalButton-upgrade',
      ...commonTrackingProperties,
    })
  }

  // Determine which channels to preselect
  // Priority: route param (single channel page) > query params (filtered all channels page)
  // When no context and user has 3 or fewer channels, preselect all (mirrors app-shell behavior)
  function getSelectedChannels(): string[] | 'all' | undefined {
    if (channelIdFromRoute) {
      return [channelIdFromRoute]
    }
    if (channelQueryParam.length > 0) {
      return channelQueryParam
    }
    if (unlockedChannels.length <= 3) {
      return 'all'
    }
    return undefined
  }

  // Handler functions
  const handleCreateNewPost = (): void => {
    trackItemClick('newPost')
    void createNewPostInComposer({
      cta: PUBLISH_CTA,
      returnFocusTo: triggerRef.current,
      channels: getSelectedChannels(),
    })
  }

  const handleCreateNewIdea = (): void => {
    trackItemClick('newIdea')
    const contentPlannerView = localStorage.getItem('contentPlannerLastViewed')
    navigate({
      pathname: createNew.route,
      search: contentPlannerView
        ? `?source=${IDEA_CTA}&view=${contentPlannerView}`
        : `?source=${IDEA_CTA}`,
    })
  }

  const handleConnectChannel = (): void => {
    trackItemClick('connectChannel')
    const { actions } = window?.appshell || {}
    actions?.openChannelStorefront({
      cta: CONNECT_CHANNEL_CTA,
    })
  }

  const handleCreateStartPage = (): void => {
    trackItemClick('createStartPage')
  }

  const handleInviteTeamMember = (): void => {
    trackItemClick('inviteTeam')
    if (showInviteTeamUpgradePath) {
      openGlobalModal(MODALS.planSelector, {
        cta: INVITE_TEAM_CTA,
        upgradePathName: 'globalButton-upgrade',
      })
    } else if (isEssentials) {
      navigate(settingsBillingRoute.route)
    } else if (canManageTeam) {
      navigate(settingsTeamRoute.route)
    }
  }

  // Both access signals read false while their queries load, so bailing to
  // null here would drop the button from the header and push the nav up,
  // then shift it back down when access resolves. Hold the button's space
  // with a same-size placeholder until access is actually known.
  if (accountLoading || channelsLoading) {
    return <GlobalButtonSkeleton className={className} />
  }

  // Users who can connect channels (`manageChannels`) keep the button even
  // with no posting access — notably in an org with no channels connected
  // yet, where this button is the onboarding path.
  if (!canPostOnAnyChannel && !canConnectChannel) {
    return null
  }

  return (
    <GlobalButtonMenu
      triggerRef={triggerRef}
      className={className}
      isCompact={isCompact}
      showCollapsedUI={showCollapsedUI}
      hasRecoverablePost={hasRecoverablePost}
      onOpen={handleDropdownOpenChange}
    >
      <GlobalButtonCreatePostItem
        onCreateNewPost={handleCreateNewPost}
        hasRecoverablePost={hasRecoverablePost}
      />
      <GlobalButtonCreateIdeaItem onCreateNewIdea={handleCreateNewIdea} />
      <GlobalButtonMenuSeparator />
      <GlobalButtonConnectChannelItem
        canConnectChannel={canConnectChannel}
        onConnectChannel={handleConnectChannel}
      />
      {/* New Start Pages can no longer be created once the sunset is announced. */}
      {!isStartPageSunset && (
        <GlobalButtonCreateStartPageItem
          createStartPageHref={CREATE_START_PAGE_LINK}
          onCreateStartPage={handleCreateStartPage}
        />
      )}
      <GlobalButtonInviteTeamMemberItem
        canManageTeam={canManageTeam}
        showInviteTeamUpgradePath={showInviteTeamUpgradePath}
        onInviteTeamMember={handleInviteTeamMember}
      />
    </GlobalButtonMenu>
  )
}
